if (!customElements.get('elixir-motion')) {
  customElements.define('elixir-motion', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 999px)');
      this.seen = new WeakSet();
      this.registered = new WeakSet();
      this.active = new Map();
      this.pending = new Set();
      this.lastY = window.scrollY;
      this.home = this.dataset.page === 'index';
      this.dataset.motionVersion = 'editorial-sequence-3';
      this.rowClocks = new WeakMap();
      this.storyTracks = new Map();
      this.lite = Boolean(navigator.connection?.saveData ||
        (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
        (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4));
      this.dataset.motionQuality = this.lite ? 'light' : 'full';
      this.scenes = new Map();
      this.lastSceneScroll = performance.now();
      // Semantic defaults cover new Theme Editor sections without per-section scripts.
      this.selector = [
        '[data-reveal]', 'main .elixir-community__photo', 'main .ss-gallery_all-item',
        'main .elixir-community__cta', 'main .elixir-community__identity', 'main product-card', 'main .collection-card',
        'main .blog-post-card', 'main .multi-column__item',
        'main :is(h1,h2,h3,h4,h5,h6,.h1,.h2,.h3,.h4,.h5,.h6)',
        'main p', 'main .prose :is(ul,ol)', 'main .rte :is(ul,ol)', 'main img',
        'main a.button', 'main a.link', 'main .ss-ig-button',
        'main .ss-instafeed-header', 'main video-media',
        'main marquee-text', 'main accordion-disclosure',
        'main details:not(accordion-disclosure details)',
        'main .collection-toolbar',
        '.footer__block', '.footer__aside > .payment-methods',
        '.footer__inner > .social-media'
      ].join(',');
      this.excluded = [
        '[data-reveal="off"]', '[data-reveal-skip]', '[hidden]', '[aria-hidden="true"]',
        'header:not(.section-header)', '.header', '.header-sidebar', '[role="dialog"]',
        'dialog', '.drawer', '.modal', '.popover', 'quick-buy-modal', 'cart-drawer', 'predictive-search',
        'form', 'product-form', 'buy-buttons', 'variant-picker', 'price-list',
        '.cart-footer', '.cart-drawer', '.cart-order', '.facets',
        '.product-gallery__thumbnail-list', '.product-gallery__zoom-button',
        'model-media', 'model-viewer', 'iframe', 'button', '[role="alert"]',
        'effect-carousel', 'slideshow-carousel', 'images-with-text-scroll', 'image-parallax',
        'localization-selector', 'localization-form', '.country-selector',
        '.shopify-app-block', '.sr-only', '.icon'
      ].join(',');
      if (!('IntersectionObserver' in window) || typeof Element.prototype.animate !== 'function') return;
      this.installWheelSmoothing(signal);
      this.installGalleryTransitions(signal);
      this.installAssembly(signal);
      this.observer = new IntersectionObserver(entries => this.reveal(entries), { threshold: 0.06 });
      this.discover(document, true);
      this.measureStoryTracks();
      this.mutations = new MutationObserver(records => {
        for (const record of records) {
          for (const node of record.removedNodes) {
            if (node.nodeType !== 1) continue;
            for (const element of [node, ...node.querySelectorAll('[data-reveal]')]) {
              this.observer.unobserve(element);
              clearTimeout(this.scenes.get(element)?.safety);
              this.scenes.delete(element);
              this.registered.delete(element);
              const animations = this.active.get(element);
              if (animations) [...animations].forEach(animation => animation.cancel());
              this.active.delete(element);
            }
          }
          for (const node of record.addedNodes) if (node.nodeType === 1) this.pending.add(node);
        }
        if (!this.pending.size || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          const roots = [...this.pending];
          this.pending.clear();
          for (const root of roots) {
            if (!root.isConnected || roots.some(other => other !== root && other.contains(root))) continue;
            this.discover(root);
          }
          for (const [root, animations] of this.active) {
            if (!root.isConnected) { animations.forEach(animation => animation.cancel()); this.active.delete(root); }
          }
        });
      });
      const main = document.querySelector('main');
      if (main) this.mutations.observe(main, { childList: true, subtree: true });
      document.addEventListener('shopify:section:load', event => this.discover(event.target, true), { signal });
      document.addEventListener('shopify:section:select', event => this.finishWithin(event.target), { signal });
      document.addEventListener('shopify:block:select', event => this.finishWithin(event.target), { signal });
      document.addEventListener('shopify:section:unload', event => this.finishWithin(event.target), { signal });
      for (const type of ['focusin', 'pointerdown']) {
        document.addEventListener(type, event => this.finishWithin(event.target), { passive: true, signal });
      }
      this.reduced.addEventListener('change', () => {
        this.finishAll();
        this.discover(document, true);
      }, { signal });
      this.mobile.addEventListener('change', () => this.finishAll(), { signal });
      window.addEventListener('pagehide', () => this.finishAll(), { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) { this.lastY = window.scrollY; this.discover(document, true); }
      }, { signal });
    }
    installGalleryTransitions(signal) {
      const pending = new WeakMap();
      const indexes = new WeakMap();
      document.addEventListener('carousel:change', event => {
        const carousel = event.target;
        if (!carousel.matches?.('product-gallery .product-gallery__carousel')) return;
        const cell = event.detail?.cell, index = event.detail?.index;
        if (!cell || !Number.isInteger(index)) return;
        const previous = indexes.get(carousel) ?? 0;
        indexes.set(carousel, index);
        if (previous === index) return;
        this.finishWithin(carousel);
        pending.set(carousel, { cell, direction: index < previous ? 'gallery-prev' : 'gallery-next' });
      }, { capture: true, signal });
      document.addEventListener('carousel:settle', event => {
        const carousel = event.target, transition = pending.get(carousel);
        if (!transition || transition.cell !== event.detail?.cell) return;
        pending.delete(carousel);
        if (this.reduced.matches || !transition.cell.isConnected ||
            transition.cell.getAttribute('data-media-type') !== 'image' ||
            transition.cell.closest('[hidden],[aria-hidden="true"],[role="dialog"]')) return;
        const photo = transition.cell.querySelector('img');
        if (!photo) return;
        this.finishWithin(photo);
        this.seen.add(photo);
        this.observer?.unobserve(photo);
        photo.dataset.galleryTransition = transition.direction;
        this.play(photo, transition.direction, 0);
      }, { capture: true, signal });
    }
    installWheelSmoothing(signal) {
      const desktop = matchMedia('(hover: hover) and (pointer: fine)');
      let frame = 0, target = window.scrollY, expected = window.scrollY, previous = 0;
      const enabled = () => desktop.matches && !this.mobile.matches && !this.reduced.matches && !window.Shopify?.designMode;
      const stop = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        target = expected = window.scrollY;
        this.dataset.scrollState = 'idle';
      };
      this.stopWheel = stop;
      const refresh = () => {
        stop();
        this.dataset.scrollMode = enabled() ? 'smooth-wheel' : 'native';
      };
      const tick = now => {
        try {
          if (!enabled() || document.hidden || Math.abs(window.scrollY - expected) > 2) { stop(); return; }
          const dt = Math.min(50, Math.max(1, now - previous));
          previous = now;
          const current = window.scrollY;
          const gap = target - current;
          const next = Math.abs(gap) < 1 ? target : current + gap * (1 - Math.exp(-dt / 85));
          window.scrollTo({ top: next, behavior: 'instant' });
          expected = window.scrollY;
          if (Math.abs(target - expected) <= 1 || expected === current) {
            window.scrollTo({ top: target, behavior: 'instant' });
            stop();
          } else frame = requestAnimationFrame(tick);
        } catch { stop(); }
      };
      window.addEventListener('wheel', event => {
        if (!enabled() || event.defaultPrevented || !event.cancelable || event.ctrlKey || event.metaKey || event.shiftKey ||
            Math.abs(event.deltaX) > Math.abs(event.deltaY) || !event.deltaY) { stop(); return; }
        try {
          if (/(hidden|clip)/.test(getComputedStyle(document.body).overflowY) ||
              /(hidden|clip)/.test(getComputedStyle(document.documentElement).overflowY)) { stop(); return; }
          for (const node of event.composedPath()) {
            if (!(node instanceof Element) || node === document.body || node === document.documentElement) continue;
            if (node.matches('input,textarea,select,[contenteditable],dialog,[role="dialog"],.drawer,.popover,iframe')) { stop(); return; }
            if (node.scrollHeight > node.clientHeight + 1 && /(auto|scroll|overlay)/.test(getComputedStyle(node).overflowY)) { stop(); return; }
          }
          const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerHeight : 1);
          const max = Math.max(0, document.documentElement.scrollHeight - innerHeight);
          const current = window.scrollY;
          if (!frame || (target - current) * delta < 0 || Math.abs(current - expected) > 2) target = current;
          const travel = innerHeight * .9;
          target = Math.max(0, Math.min(max, Math.max(current - travel, Math.min(current + travel, target + delta))));
          if (Math.abs(target - current) < .5) { stop(); return; }
          if (!frame) {
            previous = performance.now();
            expected = current;
            frame = requestAnimationFrame(tick);
          }
          event.preventDefault();
          this.dataset.scrollState = 'running';
        } catch { stop(); }
      }, { passive: false, signal });
      for (const type of ['pointerdown', 'touchstart', 'keydown', 'blur', 'resize', 'pagehide']) {
        window.addEventListener(type, stop, { passive: true, signal });
      }
      document.addEventListener('visibilitychange', stop, { signal });
      desktop.addEventListener('change', refresh, { signal });
      this.mobile.addEventListener('change', refresh, { signal });
      this.reduced.addEventListener('change', refresh, { signal });
      refresh();
    }
    typeOf(element) {
      if (element.matches('.elixir-community__photo,.ss-gallery_all-item')) return 'assembly';
      if (element.matches('product-card')) return 'card';
      if (element.matches('h1,h2,h3,h4,h5,h6,.h1,.h2,.h3,.h4,.h5,.h6,.ss-instafeed-header')) return 'title';
      if (element.matches('img,picture')) return 'image';
      if (element.matches('.collection-card,.blog-post-card,.multi-column__item')) return 'editorial';
      if (element.matches('marquee-text,video-media,accordion-disclosure,details,.collection-toolbar')) return 'subtle';
      return 'text';
    }
    discover(root, initial = false) {
      const candidates = [...root.querySelectorAll(this.selector)];
      if (root.matches?.(this.selector)) candidates.unshift(root);
      const viewport = window.innerHeight;
      for (const element of candidates) {
        if (element.dataset.reveal === 'stagger') {
          for (const child of element.children) {
            if (!child.hasAttribute('data-reveal')) child.dataset.reveal = this.typeOf(child);
            if (!candidates.includes(child)) candidates.push(child);
          }
          continue;
        }
        if (this.seen.has(element) || element.closest(this.excluded)) continue;
        // Only editorial blocks inside product-info; never animate a purchase control.
        if (element.closest('product-info,.product-info') && !element.closest('[data-block-type="description"],[data-block-type="accordion"],[data-block-type="text"],[data-block-type="image"]')) continue;
        if (element.matches('.footer__block,.multi-column__item') && element.querySelector('form')) continue;
        if (element.matches('img') && element.closest('.footer')) continue;
        if (element.matches('p') && !element.textContent.trim()) continue;
        const parent = element.parentElement?.closest('.elixir-community__photo,.ss-gallery_all-item,[data-reveal]:not([data-reveal="off"]),product-card,.multi-column__item,.collection-card,.blog-post-card,accordion-disclosure,marquee-text,video-media');
        if (parent && parent.dataset.reveal !== 'stagger') continue;
        const nativeOwner = element.closest('[reveal-on-scroll]');
        if (nativeOwner && !nativeOwner.matches('product-card,blog-posts,collection-banner,image-with-text-overlay,multiple-media-with-text') && !nativeOwner.closest('image-with-text,media-grid')) continue;
        element.dataset.reveal ||= this.typeOf(element);
        const initialRect = initial ? element.getBoundingClientRect() : null;
        const initialTop = initialRect ? initialRect.top : viewport;
        const entranceTitle = element.matches('h1,h2,h3,.ss-instafeed-header,.ss-instafeed-hashtag');
        if (this.reduced.matches || (initial && initialRect.width > 0 && initialRect.height > 0 && (initialTop < 0 || (initialTop < viewport * .82 && !entranceTitle)))) {
          this.seen.add(element);
          element.dataset.revealState = 'done';
          this.observer.unobserve(element);
          continue;
        }
        if (this.registered.has(element)) continue;
        element.dataset.revealState = 'ready';
        this.registered.add(element);
        this.observer.observe(element);
      }
    }
    measureStoryTracks() {
      for (const [root] of this.storyTracks) if (!root.isConnected) this.storyTracks.delete(root);
      for (const root of document.querySelectorAll('.elixir-community:has(.elixir-community__progress)')) {
        const rect = root.getBoundingClientRect();
        this.storyTracks.set(root, { start: window.scrollY + rect.top,
          distance: Math.max(1, rect.height - window.innerHeight), progress: -1 });
      }
      this.updateStoryTracks(window.scrollY);
    }
    updateStoryTracks(scroll) {
      for (const [root, track] of this.storyTracks) {
        const progress = this.reduced.matches ? 1 : Math.max(0, Math.min(1, (scroll - track.start) / track.distance));
        if (Math.abs(progress - track.progress) < .001) continue;
        root.style.setProperty('--ec-progress', progress.toFixed(4));
        track.progress = progress;
      }
    }
    rowClock(group, rect, family) {
      const now = performance.now();
      const top = rect.top + window.scrollY;
      const rows = (this.rowClocks.get(group) || []).filter(row => now - row.started < 3000);
      let clock = rows.find(row => Math.abs(row.top - top) < 16);
      if (!clock) {
        clock = { top, startY: window.scrollY, started: now, last: now, progress: 0,
          travel: Math.min(window.innerHeight * (this.mobile.matches ? .48 : .62), rect.height * .9),
          duration: family ? (this.mobile.matches ? 880 : 1200) : (this.mobile.matches ? 620 : 820) };
        rows.push(clock);
      }
      this.rowClocks.set(group, rows.slice(-8));
      return clock;
    }
    reveal(entries) {
      const downward = window.scrollY >= this.lastY;
      this.lastY = window.scrollY;
      const groups = new Map();
      const bounds = new Map();
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
      for (const entry of visible) {
        const element = entry.target;
        if (this.seen.has(element) || !element.isConnected) { this.observer.unobserve(element); continue; }
        if (element.closest(this.excluded)) continue;
        this.seen.add(element);
        this.observer.unobserve(element);
        element.dataset.revealState = 'done';
        if (this.reduced.matches || !downward || element.contains(document.activeElement)) continue;
        if (entry.boundingClientRect.width === 0 || entry.boundingClientRect.height === 0) continue;
        const group = element.closest('product-list,.elixir-community__mosaic,[data-reveal="stagger"],.footer__block-list,.section-header,.multi-column,.ss-isntafeed-img-wrapper,.ss-instafeed-container') || element.parentElement;
        const row = groups.get(group);
        const index = row && Math.abs(row.top - entry.boundingClientRect.top) < 12 ? row.index + 1 : 0;
        groups.set(group, { top: entry.boundingClientRect.top, index });
        const type = element.dataset.reveal;
        const family = this.isFamilyPhoto(element);
        const interval = family ? (this.mobile.matches ? 80 : 150) : this.mobile.matches ? 65 : type === 'assembly' ? 120 : 100;
        let delay = Math.min(index, this.mobile.matches ? 2 : 3) * interval;
        const rect = entry.boundingClientRect;
        const center = rect.left + rect.width / 2;
        const siblings = element.parentElement?.children;
        const ordinal = siblings ? Array.prototype.indexOf.call(siblings, element) : index;
        if (group && !bounds.has(group)) bounds.set(group, group.getBoundingClientRect());
        const groupBounds = bounds.get(group);
        const middle = groupBounds?.width > 0 ? groupBounds.left + groupBounds.width / 2 : window.innerWidth / 2;
        let side = center < middle - 24 ? -1 : center > middle + 24 ? 1 : ordinal % 2 ? 1 : -1;
        if (!family && element.dataset.revealDirection === 'left') side = -1;
        if (!family && element.dataset.revealDirection === 'right') side = 1;
        if (family) element.dataset.revealDirection = side < 0 ? 'left' : 'right';
        const rise = type === 'image' && !element.closest('.shop-the-look__image-wrapper') && ordinal % 3 === 0;
        element.dataset.revealDirection ||= rise ? 'up' : side < 0 ? 'left' : 'right';
        const rowCenter = family && !this.mobile.matches && Math.abs(center - middle) <= Math.max(24, rect.width * .3);
        if (rowCenter) { side = 0; element.dataset.revealDirection = 'up'; }
        if (family) delay = (rowCenter ? 2 : side > 0 ? 1 : 0) * (this.mobile.matches ? 80 : 140);
        const stage = element.closest('.shopify-section,.footer');
        if (stage) {
          stage.dataset.motionSequence = 'editorial';
          const phase = type === 'title' ? 0 : type === 'image' || type === 'assembly' ? 1 : element.matches('a') ? 3 : 2;
          element.dataset.motionStep = String(phase);
          if (!family && !['card', 'subtle'].includes(type)) delay += phase * (this.mobile.matches ? 50 : 80);
        }
        const context = { side: rise ? 0 : side, rect, ordinal,
          clock: family ? this.rowClock(group, rect, true) : null };
        const assembly = type === 'assembly' || (type === 'image' && element.closest('.shop-the-look__image-wrapper'));
        if (assembly && !this.lite && this.scenes.size < (this.mobile.matches ? 4 : 8)) {
          this.playAssembly(element, type, delay, context);
        } else this.play(element, type, this.lite ? delay / 2 : delay, context);
      }
    }
    isFamilyPhoto(element) {
      return element.matches('.elixir-community__photo,.ss-gallery_all-item') &&
        Boolean(document.querySelector('main .elixir-community[data-motion-profile="family-dramatic"]'));
    }
    installAssembly(signal) {
      window.addEventListener('scroll', () => {
        this.lastSceneScroll = performance.now();
        if ((this.scenes.size || this.storyTracks.size) && !this.sceneFrame) this.sceneFrame = requestAnimationFrame(now => this.updateAssembly(now));
      }, { passive: true, signal });
      const measure = () => {
        cancelAnimationFrame(this.trackMeasureFrame);
        this.trackMeasureFrame = requestAnimationFrame(() => { this.trackMeasureFrame = 0; this.measureStoryTracks(); });
      };
      window.addEventListener('resize', () => { this.finishAll(); measure(); }, { passive: true, signal });
      document.addEventListener('shopify:section:load', measure, { signal });
      document.addEventListener('shopify:section:unload', measure, { signal });
      this.reduced.addEventListener('change', measure, { signal });
      if ('ResizeObserver' in window) {
        this.trackResize = new ResizeObserver(measure);
        const main = document.querySelector('main');
        if (main) this.trackResize.observe(main);
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.finishAll(); }, { signal });
    }
    playAssembly(element, type, delay, context) {
      const animations = this.play(element, type, delay, context);
      if (!animations?.length) return;
      const now = performance.now();
      const family = this.isFamilyPhoto(element);
      const depth = .9 + ((context.ordinal || 0) % 3) * .12;
      const travel = family ? (this.mobile.matches ? .42 : .58) : (this.mobile.matches ? .30 : .38);
      const scene = { animations, clock: context.clock, startY: window.scrollY, travel: window.innerHeight * travel * depth,
        duration: family ? (this.mobile.matches ? 820 : 1100) : (this.mobile.matches ? 620 : 820),
        delay, progress: 0, started: now, last: now };
      try {
        for (const animation of animations) { animation.pause(); animation.currentTime = 0; }
        this.scenes.set(element, scene);
        element.dataset.motionScene = family ? 'family-row-sequence' : 'assemble';
        this.lastSceneScroll = now;
        // A paused animation must never survive a controller failure.
        scene.safety = setTimeout(() => {
          animations.forEach(animation => animation.cancel());
          this.scenes.delete(element);
          element.dataset.revealState = 'done';
        }, family ? 2600 : 1700);
        if (!this.sceneFrame) this.sceneFrame = requestAnimationFrame(time => this.updateAssembly(time));
      } catch { animations.forEach(animation => animation.cancel()); this.scenes.delete(element); }
    }
    updateAssembly(now) {
      this.sceneFrame = 0;
      try {
        if (this.reduced.matches || document.hidden) { this.finishAll(); return; }
        const scroll = window.scrollY;
        this.updateStoryTracks(scroll);
        const frameGap = now - (this.previousSceneFrame || now);
        this.previousSceneFrame = now;
        if (frameGap > 34 && frameGap < 200) this.slowFrames = (this.slowFrames || 0) + 1;
        for (const [element, scene] of this.scenes) {
          if (!element.isConnected || !this.active.has(element)) {
            clearTimeout(scene.safety); this.scenes.delete(element); continue;
          }
          const clock = scene.clock || scene;
          const dt = Math.max(0, now - clock.last);
          clock.last = now;
          const progress = (scroll - clock.startY) / Math.max(1, clock.travel);
          const idle = now - this.lastSceneScroll > 160;
          clock.progress = Math.min(1, Math.max(clock.progress, progress,
            clock.progress + (idle ? Math.min(dt, 50) / clock.duration : 0)));
          scene.progress = clock.progress;
          const time = scene.progress * (clock.duration + (scene.clock ? (this.mobile.matches ? 80 : 280) : scene.delay));
          scene.animations.forEach(animation => { animation.currentTime = time; });
          if (scene.progress >= 1) {
            clearTimeout(scene.safety);
            this.scenes.delete(element);
            scene.animations.forEach(animation => animation.cancel());
          }
        }
        if (this.slowFrames >= 12) {
          this.lite = true; this.dataset.motionQuality = 'light'; this.finishAll(); return;
        }
        if (this.scenes.size) this.sceneFrame = requestAnimationFrame(time => this.updateAssembly(time));
      } catch { this.finishAll(); }
    }
    play(element, type, delay, context = {}) {
      if (this.reduced.matches) return [];
      const mobile = this.mobile.matches;
      const intensity = this.lite ? .6 : 1;
      const side = context.side ?? 1;
      const x = (mobile ? 52 : 140) * side * intensity;
      const y = (mobile ? 42 : 100) * intensity;
      const scale = 1 + (mobile ? .065 : .12) * intensity;
      const family = this.isFamilyPhoto(element);
      const easing = family ? 'cubic-bezier(.32,.12,.18,1)' : 'cubic-bezier(.25,.75,.25,1)';
      let frames = [{ opacity: .12, transform: `translate3d(${x * .42}px,${y * .6}px,0)` }, { opacity: 1, transform: 'none' }];
      let duration = mobile ? 520 : 680;
      if (family) {
        const width = context.rect?.width || (mobile ? 180 : 360);
        const distance = (mobile ? Math.min(120, Math.max(70, width * .48)) : Math.min(460, Math.max(260, width * .9))) * intensity;
        const lift = (mobile ? 64 : 140) * intensity;
        const startScale = 1 - (mobile ? .12 : .20) * intensity;
        frames = [
          { offset: 0, opacity: .08, clipPath: 'inset(0)', transform: `translate3d(${distance * side}px,${lift}px,0) scale(${startScale})` },
          { offset: .72, opacity: 1, clipPath: 'inset(0)', transform: `translate3d(${distance * side * .12}px,${lift * .12}px,0) scale(.98)` },
          { offset: 1, opacity: 1, clipPath: 'inset(0)', transform: 'none' }
        ];
        duration = mobile ? 880 : 1200;
      } else if (type === 'gallery-next' || type === 'gallery-prev') {
        const direction = type === 'gallery-prev' ? -1 : 1;
        frames = [
          { opacity: .3, clipPath: 'inset(4%)', transform: `translateX(${direction * (mobile ? 40 : 84) * intensity}px) scale(${1 + (mobile ? .05 : .1) * intensity})` },
          { opacity: 1, clipPath: 'inset(0)', transform: 'none' }
        ];
        duration = mobile ? 520 : 720;
      } else if (type === 'subtle') {
        frames = [{ opacity: .55 }, { opacity: 1 }];
        duration = 350;
      } else if (type === 'title') {
        frames = [
          { opacity: .2, clipPath: 'inset(0 0 100% 0)', transform: `translate3d(${-x * .4}px,${mobile ? 42 * intensity : 90 * intensity}px,0)` },
          { opacity: 1, clipPath: 'inset(0)', transform: 'none' }
        ];
        duration = mobile ? 600 : 760;
      } else if (type === 'image' || type === 'assembly') {
        frames = [
          { opacity: .1, clipPath: `inset(${mobile ? 4 : 8}% ${mobile ? 3 : 5}%)`, transform: `translate3d(${x}px,${y}px,0) scale(${scale})` },
          { opacity: 1, clipPath: 'inset(0)', transform: 'none' }
        ];
        duration = mobile ? 620 : 820;
      } else if (type === 'editorial') {
        frames = [{ opacity: .12, clipPath: 'inset(0 0 30% 0)', transform: `translate3d(${x * .65}px,${y * .7}px,0)` },
          { opacity: 1, clipPath: 'inset(0)', transform: 'none' }];
        duration = mobile ? 580 : 760;
      }
      if (this.lite) {
        frames = frames.map(({ clipPath, ...frame }) => frame);
        duration = Math.min(duration, 520);
      }
      element.dataset.revealState = 'running';
      const animations = [];
      this.active.set(element, animations);
      const animate = (target, keyframes, timing) => {
        if (!target) return;
        try {
          const animation = target.animate(keyframes, { ...timing, easing, fill: 'backwards' });
          animations.push(animation);
          const clear = () => {
            const index = animations.indexOf(animation);
            if (index >= 0) animations.splice(index, 1);
            if (!animations.length) {
              this.active.delete(element); element.dataset.revealState = 'done';
              const scene = this.scenes.get(element);
              if (scene) { clearTimeout(scene.safety); this.scenes.delete(element); }
            }
          };
          animation.onfinish = clear;
          animation.oncancel = clear;
        } catch { this.active.delete(element); element.dataset.revealState = 'done'; }
      };
      if (type === 'card') {
        const cardFrames = [
          { opacity: .1, transform: `translate3d(${x * .48}px,${y}px,0) scale(${1 + (mobile ? .03 : .055) * intensity})` },
          { opacity: 1, transform: 'none' }
        ];
        animate(element.querySelector('.product-card__media'), cardFrames, {
          duration: this.lite ? 480 : mobile ? 580 : 780, delay
        });
        // Prices and quick-buy remain in their final positions.
        animate(element.querySelector('.product-title'), [
          { opacity: .25, transform: `translateY(${mobile ? 18 : 28}px)` },
          { opacity: 1, transform: 'none' }
        ], { duration: mobile ? 440 : 560, delay: delay + (mobile ? 50 : 90) });
      } else animate(element, frames, { duration, delay });
      if (!animations.length) { this.active.delete(element); element.dataset.revealState = 'done'; }
      return animations;
    }
    finishWithin(target) {
      for (const [root, animations] of this.active) {
        if (root.contains(target) || target.contains?.(root)) {
          [...animations].forEach(animation => animation.cancel());
          this.active.delete(root);
        }
      }
      const candidate = target.closest?.('[data-reveal]');
      if (candidate && candidate.dataset.reveal !== 'stagger') { this.seen.add(candidate); candidate.dataset.revealState = 'done'; this.observer?.unobserve(candidate); }
    }
    finishAll() {
      cancelAnimationFrame(this.sceneFrame);
      this.sceneFrame = 0;
      this.previousSceneFrame = 0;
      for (const scene of this.scenes?.values() || []) clearTimeout(scene.safety);
      this.scenes?.clear();
      for (const animations of this.active.values()) [...animations].forEach(animation => animation.cancel());
      this.active.clear();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.trackMeasureFrame);
      this.trackResize?.disconnect();
      this.storyTracks?.clear();
      this.stopWheel?.();
      this.abort?.abort();
      this.observer?.disconnect();
      this.mutations?.disconnect();
      cancelAnimationFrame(this.frame);
      this.finishAll();
      this.pending?.clear();
    }
  });
}


(() => {
  if (window.elixirPhotoFloatReady) return;
  window.elixirPhotoFloatReady = true;
  const isHomepage = Boolean(document.querySelector('elixir-motion[data-page="index"]'));
  const homeTextSelector = isHomepage ? ', #main .slideshow__slide-content .prose > :is(p,h1,h2), #main .countdown-container, #main product-card .product-title, #main product-card price-list' : '';
  const textSelector = '.elixir-history__copy > :is(h2,p,div), .elixir-history__intro > :is(h1,p,div), .elixir-history__closing > :is(h2,p), .elixir-community__title, .elixir-community__subtitle, .elixir-community__text, .elixir-community__identity, .elixir-community__footnote, .elx-loyalty h1, .elx-loyalty h2, .elx-loyalty h3, .elx-loyalty p' + homeTextSelector + ', .elixir-password .small-text, .elixir-password .main-title, .elixir-password .countdown-title, .elixir-password .countdown, .elixir-password .elixir-password__floating-text';
  const uiSelector = '.header__logo, .header__primary-nav-item > a, .header__menu-disclosure > summary, .header__primary-nav > button, .header__secondary-nav a, .header__secondary-nav button, .footer form, .newsletter form, .footer .social-media, .footer .payment-methods';
  const extraText = '.footer :is(h2,h3,p), .footer .link-faded, .newsletter :is(h2,h3,p), .ss-instafeed-header, .ss-instafeed-hashtag, #main product-card .product-title, #main product-card price-list';
  const extraMedia = '.ss-gallery_all-item img, #main product-card .product-card__media';
  const selector = '.elixir-history__figure:not(.elixir-history__video) img, .elixir-community__photo img, .elx-loyalty img:not(.elx-loyalty__face img), .elx-loyalty__perspective, ' + textSelector + ', ' + uiSelector + ', ' + extraText + ', ' + extraMedia + (isHomepage ? ', #main product-card .product-card__media' : '');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mouse = matchMedia('(hover: hover) and (pointer: fine)');
  const style = document.createElement('style');
  style.textContent = `
    @keyframes elixir-photo-float {
      0%,100% { translate:0 0; rotate:calc(var(--ep-roll,1deg) * -1); }
      50% { translate:var(--ep-drift,5px) var(--ep-lift,-24px); rotate:var(--ep-roll,1deg); }
    }
    @media (prefers-reduced-motion:no-preference) {
      .elixir-history__figure img.elixir-photo-float,
      .elixir-community__photo img.elixir-photo-float,
      .elixir-photo-float.elixir-float-text,
      .elx-loyalty .elixir-photo-float,
      #main .product-card__media.elixir-photo-float,
      .elixir-photo-float.elixir-float-extra {
        animation:elixir-photo-float var(--ep-duration,6s) ease-in-out var(--ep-phase,0s) infinite;
        animation-play-state:paused;
        transform:perspective(800px) rotateX(var(--ep-x,3deg)) rotateY(var(--ep-y,-3deg)) translateZ(var(--ep-depth,12px));
        box-shadow:0 22px 38px -12px rgba(0,0,0,.7), 0 6px 12px rgba(0,0,0,.32), 0 0 0 1px rgba(255,255,255,.16);
        transform-origin:center;
        transition:transform 220ms ease-out;
        backface-visibility:hidden;
      }
      .elixir-history__figure img.elixir-photo-float[data-float-visible],
      .elixir-community__photo img.elixir-photo-float[data-float-visible],
      .elixir-photo-float.elixir-float-text[data-float-visible],
      .elx-loyalty .elixir-photo-float[data-float-visible],
      #main .product-card__media.elixir-photo-float[data-float-visible],
      .elixir-photo-float.elixir-float-extra[data-float-visible] { animation-play-state:running; }
    }
    .elixir-photo-float.elixir-float-text {
      --ep-lift:-16px; --ep-drift:3px; --ep-roll:.5deg;
      --ep-x:0deg; --ep-y:0deg; --ep-depth:0px;
      box-shadow:none; text-shadow:0 8px 18px rgba(0,0,0,.2);
    }
    .elixir-password__floating-text { display:inline-block; }
    .elixir-password .elixir-float-text {
      --ep-lift:-12px; --ep-drift:3px; --ep-roll:.35deg;
      text-shadow:0 3px 2px rgba(0,0,0,.3),0 12px 24px rgba(0,0,0,.55);
    }
    .elixir-password .countdown.elixir-float-text { --ep-lift:-16px; --ep-roll:.2deg; }
    @media (max-width:749px) {
      .elixir-password .elixir-float-text { --ep-lift:-6px; --ep-drift:1px; --ep-roll:.15deg; }
      .elixir-password .countdown.elixir-float-text { --ep-lift:-8px; }
    }
    .elx-loyalty .elx-loyalty__perspective.elixir-photo-float {
      transform:none; backface-visibility:visible; box-shadow:none;
    }
    #main .product-card__media.elixir-photo-float {
      --ep-x:0deg; --ep-y:0deg; --ep-depth:0px; --ep-lift:-18px;
      --ep-roll:.6deg; box-shadow:none;
    }
    #main product-card price-list.elixir-photo-float {
      --ep-lift:-8px; --ep-drift:1px; --ep-roll:.2deg; text-shadow:none;
    }
    #main .countdown-container.elixir-photo-float { --ep-roll:.3deg; }
    .elixir-photo-float.elixir-float-ui {
      --ep-lift:-4px; --ep-drift:1px; --ep-roll:.2deg;
      --ep-x:0deg; --ep-y:0deg; --ep-depth:0px;
      box-shadow:none; text-shadow:none;
    }
    .elixir-photo-float.elixir-float-ui:focus-within,
    .header:has(details[open]) .elixir-float-ui,
    .elixir-float-ui[aria-expanded="true"] {
      animation-play-state:paused!important;
      transform:none!important; rotate:none!important; translate:none!important;
    }
    @media (max-width:749px) {
      .elixir-photo-float { --ep-lift:-14px; --ep-drift:3px; --ep-roll:.6deg; --ep-depth:0px; }
    }
    @media (prefers-reduced-motion:reduce) {
      .elixir-photo-float { animation:none!important; transform:none!important; translate:none!important; rotate:none!important; box-shadow:none!important; }
    }
  `;
  document.head.appendChild(style);
  const registered = new Set();
  const observer = new IntersectionObserver(entries => entries.forEach(({target,isIntersecting}) => {
    target.toggleAttribute('data-float-visible',isIntersecting && !document.hidden && !reduced.matches);
    if (!isIntersecting) reset(target);
  }), {threshold:0.05});
  let active = null, frame = 0, point = null;
  function reset(img) {
    img.style.removeProperty('--ep-x');
    img.style.removeProperty('--ep-y');
    img.style.removeProperty('--ep-depth');
    if (active === img) { active = null; cancelAnimationFrame(frame); frame = 0; }
  }
  function scan() {
    registered.forEach(img => {
      if (!img.isConnected) { observer.unobserve(img); registered.delete(img); if (active === img) reset(img); }
    });
    document.querySelectorAll(selector).forEach(img => {
      if (registered.has(img) || img.parentElement.closest('.elixir-photo-float')) return;
      img.style.setProperty('--ep-duration', (5.6 + registered.size % 4 * .45) + 's');
      img.style.setProperty('--ep-phase', (-registered.size % 5 * .7) + 's');
      img.classList.add('elixir-photo-float');
      img.classList.add('elixir-float-extra');
      if (img.matches(uiSelector)) img.classList.add('elixir-float-ui');
      if (img.matches(textSelector + ', ' + extraText)) img.classList.add('elixir-float-text');
      registered.add(img); observer.observe(img);
    });
  }
  document.addEventListener('pointermove', event => {
    if (!mouse.matches || reduced.matches || event.pointerType !== 'mouse') return;
    const img = event.target.closest?.('.elixir-photo-float');
    if (active && active !== img) reset(active);
    if (!img || img.matches('.elx-loyalty__perspective')) return;
    active = img; point = {x:event.clientX,y:event.clientY};
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!active) return;
      const bounds = active.parentElement.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const x = Math.max(-1,Math.min(1,(point.x-bounds.left)/bounds.width*2-1));
      const y = Math.max(-1,Math.min(1,(point.y-bounds.top)/bounds.height*2-1));
      const strength = active.classList.contains('elixir-float-ui') ? .18 : active.classList.contains('elixir-float-text') ? .55 : 1;
      active.style.setProperty('--ep-depth',(26 * strength)+'px');
      active.style.setProperty('--ep-x',(-y*12*strength).toFixed(2)+'deg');
      active.style.setProperty('--ep-y',(x*15*strength).toFixed(2)+'deg');
    });
  }, {passive:true});
  document.addEventListener('pointerout', event => {
    if (active && !active.contains(event.relatedTarget)) reset(active);
  }, {passive:true});
  window.addEventListener('blur', () => { if(active) reset(active); });
  document.addEventListener('visibilitychange', () => {
    if(active) reset(active);
    registered.forEach(img => { img.removeAttribute('data-float-visible'); observer.unobserve(img); observer.observe(img); });
  });
  reduced.addEventListener('change', () => {
    if(active) reset(active);
    registered.forEach(img => { img.removeAttribute('data-float-visible'); observer.unobserve(img); observer.observe(img); });
  });
  let scanFrame = 0;
  const additions = new MutationObserver(records => {
    if (!records.some(r => [...r.addedNodes, ...r.removedNodes].some(n => n.nodeType === 1)) || scanFrame) return;
    scanFrame = requestAnimationFrame(() => { scanFrame = 0; scan(); });
  });
  const watch = () => additions.observe(document.body, {childList:true,subtree:true});
  if(document.body) watch(); else document.addEventListener('DOMContentLoaded',watch,{once:true});
  document.addEventListener('shopify:section:load',scan);
  document.addEventListener('shopify:section:unload',scan);
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',scan,{once:true});
  else scan();
})();
